import React, { useState } from 'react';
import { Button, ButtonGroup, Col, Row } from 'react-bootstrap';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { getHealthRange } from '../api/trainer';
import ChartCard from '../components/ChartCard';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { isoDaysAgo, shortDate } from '../utils/format';

const RANGES = [7, 30, 90];
const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 8, right: 8, bottom: 0, left: -12 };

const HealthPage: React.FC = () => {
  const [days, setDays] = useState(30);

  const data = useAsync(async () => {
    const range = { from: isoDaysAgo(days), to: isoDaysAgo(0) };
    const [daily, sleep, hrv, battery] = await Promise.all([
      getHealthRange('daily', range),
      getHealthRange('sleep', range),
      getHealthRange('hrv', range),
      getHealthRange('body-battery', range),
    ]);
    return {
      sleepRows: sleep.map(n => ({
        date: shortDate(n.date),
        hours: n.totalSleepS == null ? null : Number((n.totalSleepS / 3600).toFixed(2)),
        score: n.sleepScore,
      })),
      hrvRows: hrv.map(d => ({ date: shortDate(d.date), lastNight: d.lastNightAvg, weekly: d.weeklyAvg })),
      restingRows: daily.map(d => ({ date: shortDate(d.date), resting: d.restingHr })),
      stepRows: daily.map(d => ({ date: shortDate(d.date), steps: d.steps })),
      stressRows: daily.map(d => ({ date: shortDate(d.date), stress: d.stressAvg })),
      batteryRows: battery.map(d => ({ date: shortDate(d.date), high: d.highest, low: d.lowest })),
    };
  }, [days]);

  const rows = data.data;
  const empty = (list: unknown[] | undefined) => !data.loading && (list?.length ?? 0) === 0;

  return (
    <>
      <PageHeader title="Health">
        <p>Sleep, heart rate variability, resting heart rate and recovery.</p>
      </PageHeader>

      <div className="d-flex justify-content-end mb-3">
        <ButtonGroup aria-label="Date range">
          {RANGES.map(r => (
            <Button key={r} variant={days === r ? 'primary' : 'outline-secondary'} onClick={() => setDays(r)} aria-pressed={days === r}>
              {r} days
            </Button>
          ))}
        </ButtonGroup>
      </div>

      <StatusMessage loading={data.loading} error={data.error} onRetry={data.reload} />

      {rows && (
        <Row className="g-3">
          <Col md={6}>
            <ChartCard title="Sleep (hours)" empty={empty(rows.sleepRows)}>
              <BarChart data={rows.sleepRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...AXIS} domain={[0, 'dataMax + 1']} />
                <Tooltip />
                <Bar dataKey="hours" fill="var(--chart-2)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Sleep score" empty={empty(rows.sleepRows)}>
              <LineChart data={rows.sleepRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...AXIS} domain={[0, 100]} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="HRV (ms)" empty={empty(rows.hrvRows)}>
              <LineChart data={rows.hrvRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...AXIS} domain={['dataMin - 5', 'dataMax + 5']} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="lastNight" name="Last night" stroke="var(--chart-1)" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="weekly" name="7-day avg" stroke="var(--chart-3)" dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Resting heart rate (bpm)" empty={empty(rows.restingRows)}>
              <LineChart data={rows.restingRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...AXIS} domain={['dataMin - 3', 'dataMax + 3']} />
                <Tooltip />
                <Line type="monotone" dataKey="resting" stroke="var(--chart-4)" dot={false} strokeWidth={2} />
              </LineChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Body battery" empty={empty(rows.batteryRows)}>
              <AreaChart data={rows.batteryRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...AXIS} domain={[0, 100]} />
                <Tooltip />
                <Area type="monotone" dataKey="high" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.25} />
                <Area type="monotone" dataKey="low" stroke="var(--chart-2)" fill="var(--chart-2)" fillOpacity={0.2} />
              </AreaChart>
            </ChartCard>
          </Col>
          <Col md={6}>
            <ChartCard title="Average stress" empty={empty(rows.stressRows)}>
              <LineChart data={rows.stressRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...AXIS} domain={[0, 100]} />
                <Tooltip />
                <Line type="monotone" dataKey="stress" name="Average" stroke="var(--chart-3)" dot={false} strokeWidth={2} />
              </LineChart>
            </ChartCard>
          </Col>
          <Col xs={12}>
            <ChartCard title="Steps" empty={empty(rows.stepRows)}>
              <BarChart data={rows.stepRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...AXIS} />
                <Tooltip />
                <Bar dataKey="steps" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartCard>
          </Col>
        </Row>
      )}
    </>
  );
};

export default HealthPage;
